// ============================================================
// src/shack/shackSurface.js
// Helpers geométricos y de superficie del Shack 3D
// ============================================================

import {
  T,
  ROOM,
  DEFAULT_RATIO,
  GRID_RES,
} from './shackConfig.js';

// ------------------------------------------------------------
// HELPERS BÁSICOS
// ------------------------------------------------------------

export function round4(n) {
  return Math.round(n * 10000) / 10000;
}

export function vec3From(v) {
  const THREE = T();
  return new THREE.Vector3(v.x, v.y, v.z);
}

export function clamp01(v, min = 0, max = 1) {
  return Math.max(min, Math.min(max, v));
}

export function clampNum(v, min, max) {
  return Math.max(min, Math.min(max, Number(v) || 0));
}

export function validRatio(r) {
  const n = Number(r);
  return (Number.isFinite(n) && n > 0.2 && n < 4) ? n : DEFAULT_RATIO;
}

function surfaceHorizontalSpan(calibration, wall) {
  const c = calibration[wall];
  const tl = vec3From(c.tl);
  const tr = vec3From(c.tr);
  const bl = vec3From(c.bl);
  const br = vec3From(c.br);
  return (tr.distanceTo(tl) + br.distanceTo(bl)) * 0.5;
}

function surfaceVerticalSpan(calibration, wall) {
  const c = calibration[wall];
  const tl = vec3From(c.tl);
  const bl = vec3From(c.bl);
  const tr = vec3From(c.tr);
  const br = vec3From(c.br);
  return (bl.distanceTo(tl) + br.distanceTo(tr)) * 0.5;
}

function estimatedMarginFromSize(calibration, sizeWorld, wall, horizontal) {
  const span = horizontal
    ? surfaceHorizontalSpan(calibration, wall)
    : surfaceVerticalSpan(calibration, wall);

  if (span <= 0.0001) return 0.02;
  const half = (sizeWorld / span) * 0.5;
  return clampNum(half, 0.01, 0.22);
}

export function safeDomainValue(v, size, wall, horizontal, calibration) {
  const margin = calibration
    ? estimatedMarginFromSize(calibration, size, wall, horizontal)
    : Math.min(0.38, size / (2 * (horizontal
      ? (wall === 'trasera' ? ROOM.trasera.ancho : ROOM.lateral.ancho)
      : ROOM.alto)));

  return clampNum(v, margin, 1 - margin);
}

// ------------------------------------------------------------
// SUPERFICIE BILINEAL
// ------------------------------------------------------------

export function surfacePoint(calibration, wall, u, v) {
  const THREE = T();
  const c = calibration[wall];
  const tl = vec3From(c.tl);
  const tr = vec3From(c.tr);
  const br = vec3From(c.br);
  const bl = vec3From(c.bl);

  const pTop = new THREE.Vector3().lerpVectors(tl, tr, u);
  const pBottom = new THREE.Vector3().lerpVectors(bl, br, u);

  return new THREE.Vector3().lerpVectors(pTop, pBottom, v);
}

export function surfaceDerivativeU(calibration, wall, u, v) {
  const eps = 0.001;
  const p1 = surfacePoint(calibration, wall, clamp01(u - eps), v);
  const p2 = surfacePoint(calibration, wall, clamp01(u + eps), v);
  return p2.sub(p1);
}

export function surfaceDerivativeV(calibration, wall, u, v) {
  const eps = 0.001;
  const p1 = surfacePoint(calibration, wall, u, clamp01(v - eps));
  const p2 = surfacePoint(calibration, wall, u, clamp01(v + eps));
  return p2.sub(p1);
}

export function surfaceAxesAt(calibration, wall, u, v) {
  const THREE = T();

  let du = surfaceDerivativeU(calibration, wall, u, v);
  let dv = surfaceDerivativeV(calibration, wall, u, v);

  if (du.lengthSq() < 1e-8) du = new THREE.Vector3(1, 0, 0);
  if (dv.lengthSq() < 1e-8) dv = new THREE.Vector3(0, -1, 0);

  const xAxis = du.clone().normalize();
  const yAxis = dv.clone().multiplyScalar(-1).normalize();
  let zAxis = new THREE.Vector3().crossVectors(xAxis, yAxis).normalize();

  if (!Number.isFinite(zAxis.x) || zAxis.lengthSq() < 1e-8) {
    zAxis = new THREE.Vector3(0, 0, 1);
  }

  return { xAxis, yAxis, zAxis };
}

export function surfaceFrameAt(calibration, wall, u, v, w, h) {
  const THREE = T();
  const uu = safeDomainValue(u, w, wall, true, calibration);
  const vv = safeDomainValue(v, h, wall, false, calibration);

  const center = surfacePoint(calibration, wall, uu, vv);
  const { xAxis, yAxis, zAxis } = surfaceAxesAt(calibration, wall, uu, vv);

  const m = new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis);
  const q = new THREE.Quaternion().setFromRotationMatrix(m);

  center.add(zAxis.clone().multiplyScalar(0.0068));

  return { center, quaternion: q };
}

export function surfaceRectPoint(
  calibration,
  wall,
  centerU,
  centerV,
  itemW,
  itemH,
  x,
  y,
  zOffset = 0
) {
  const uu = safeDomainValue(centerU, itemW, wall, true, calibration);
  const vv = safeDomainValue(centerV, itemH, wall, false, calibration);

  const spanU = Math.max(0.0001, surfaceHorizontalSpan(calibration, wall));
  const spanV = Math.max(0.0001, surfaceVerticalSpan(calibration, wall));

  const u = clamp01(uu + (x / spanU));
  const v = clamp01(vv - (y / spanV));

  const point = surfacePoint(calibration, wall, u, v);
  const { zAxis } = surfaceAxesAt(calibration, wall, u, v);

  if (zOffset) {
    point.add(zAxis.clone().multiplyScalar(zOffset));
  }

  return point;
}

export function updateWarpedMeshGeometry(mesh, calibration, wall, centerU, centerV, itemW, itemH) {
  const THREE = T();
  if (!mesh?.geometry?.attributes?.position) return;

  const warp = mesh.userData?.surfaceWarp;
  if (!warp) return;

  const posAttr = mesh.geometry.attributes.position;
  const base = mesh.userData.basePositions;

  if (!base || base.length !== posAttr.array.length) return;

  for (let i = 0; i < base.length; i += 3) {
    const localX = base[i] + (warp.offsetX || 0);
    const localY = base[i + 1] + (warp.offsetY || 0);
    const point = surfaceRectPoint(
      calibration,
      wall,
      centerU,
      centerV,
      itemW,
      itemH,
      localX,
      localY,
      warp.zOffset || 0
    );

    posAttr.array[i] = point.x;
    posAttr.array[i + 1] = point.y;
    posAttr.array[i + 2] = point.z;
  }

  posAttr.needsUpdate = true;
  mesh.geometry.computeVertexNormals();
  mesh.geometry.computeBoundingBox();
  mesh.geometry.computeBoundingSphere();

  mesh.position.set(0, 0, 0);
  mesh.rotation.set(0, 0, 0);
  mesh.quaternion.identity();
  mesh.scale.set(1, 1, 1);
  mesh.updateMatrixWorld(true);

  if (mesh.material?.map instanceof THREE.Texture) {
    mesh.material.map.needsUpdate ||= false;
  }
}

// ------------------------------------------------------------
// PROYECCIÓN
// ------------------------------------------------------------

export function projectWorldToStage(camera, imageView, vec) {
  const p = vec3From(vec);
  p.project(camera);

  const x = imageView.x + ((p.x + 1) / 2) * imageView.w;
  const y = imageView.y + ((1 - (p.y + 1) / 2)) * imageView.h;

  return { x, y };
}

export function projectPointToUV(calibration, wall, point, w, h, fallbackU = 0.5, fallbackV = 0.5) {
  let best = { u: fallbackU, v: fallbackV, d: Infinity };

  const cols = wall === 'trasera' ? GRID_RES.trasera.cols : GRID_RES.lateral.cols;
  const rows = wall === 'trasera' ? GRID_RES.trasera.rows : GRID_RES.lateral.rows;

  for (let i = 0; i <= cols; i++) {
    const u = i / cols;
    for (let j = 0; j <= rows; j++) {
      const v = j / rows;
      const p = surfacePoint(calibration, wall, u, v);
      const d = p.distanceToSquared(point);
      if (d < best.d) best = { u, v, d };
    }
  }

  best.u = safeDomainValue(best.u, w, wall, true, calibration);
  best.v = safeDomainValue(best.v, h, wall, false, calibration);

  return { u: best.u, v: best.v };
}